import React, { useState, useEffect, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";

// Import components
import HomePage from "./components/HomePage";
import MarketplacePage from "./components/MarketplacePage";
import ContactPage from "./components/ContactPage";
import EquipmentPage from "./components/EquipmentPage";
import JobsPage from "./components/JobsPage";
import AuthPage from "./components/AuthPage";
import Dashboard from "./components/Dashboard";
import ServiceDetail from "./components/ServiceDetail";
import ProductDetail from "./components/ProductDetail";
import Cart from "./components/Cart";
import Profile from "./components/Profile";
import ProviderDashboard from "./components/ProviderDashboard";
import ProviderBookings from "./components/ProviderBookings";
import PaymentRequests from "./components/PaymentRequests";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import SubscriptionPage from "./components/SubscriptionPage";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import SubscriptionCancel from "./components/SubscriptionCancel";
import AdminDashboard from "./components/AdminDashboard";
import SupervisorDashboard from "./components/SupervisorDashboard";
import Inbox from "./components/Inbox";
import JobManagement from "./components/JobManagement";
import JobPosting from "./components/JobPosting";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import EmailVerification from "./components/EmailVerification";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import MotorLifeCalculator from "./components/MotorLifeCalculator";
import AntiPunctureTires from "./components/AntiPunctureTires";

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-teal-500 flex items-center justify-center">
    <div className="text-white text-2xl">Loading D.C. Technology Corp...</div>
  </div>
);

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for saved auth state
  useEffect(() => {
    const savedUser = localStorage.getItem('dcTech_user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('dcTech_user');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('dcTech_user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('dcTech_user');
    localStorage.removeItem('dcTech_cart');
    localStorage.removeItem('dcTech_token');
  };

  const authValue = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isProvider: user?.userType === 'provider'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-teal-500 flex items-center justify-center">
        <div className="text-white text-2xl">Loading D.C. Technology Corp...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authValue}>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/equipment" element={<EquipmentPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/maintenance-prevention" element={<MotorLifeCalculator />} />
            <Route path="/anti-puncture-tires" element={<AntiPunctureTires />} />
            <Route 
              path="/auth" 
              element={!user ? <AuthPage /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/verify-email" 
              element={<EmailVerification />} 
            />
            <Route 
              path="/dashboard" 
              element={user ? <Dashboard /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/service/:id" 
              element={user ? <ServiceDetail /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/product/:id" 
              element={user ? <ProductDetail /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/cart" 
              element={user ? <Cart /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/profile" 
              element={user ? <Profile /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/provider-dashboard" 
              element={user?.userType === 'provider' || user?.userType === 'admin' ? <ProviderDashboard /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/provider-bookings" 
              element={user?.userType === 'provider' || user?.userType === 'admin' ? <ProviderBookings /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/payment-requests" 
              element={user ? <PaymentRequests /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/payment/success" 
              element={user ? <PaymentSuccess /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/payment/cancel" 
              element={user ? <PaymentCancel /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/subscription" 
              element={<SubscriptionPage />} 
            />
            <Route 
              path="/subscription/success" 
              element={user?.userType === 'provider' || user?.userType === 'admin' ? <SubscriptionSuccess /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/subscription/cancel" 
              element={user?.userType === 'provider' || user?.userType === 'admin' ? <SubscriptionCancel /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/admin" 
              element={(user?.userType === 'admin' || user?.userType === 'supervisor') ? <AdminDashboard /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/supervisor" 
              element={loading ? <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900 flex items-center justify-center"><div className="text-white text-xl">Loading...</div></div> : user?.userType === 'supervisor' ? <SupervisorDashboard /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/supervisor-dashboard" 
              element={loading ? <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900 flex items-center justify-center"><div className="text-white text-xl">Loading...</div></div> : (user?.userType === 'supervisor' || user?.userType === 'admin') ? <SupervisorDashboard /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/provider" 
              element={user?.userType === 'provider' ? <ProviderDashboard /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/inbox" 
              element={user ? <Inbox /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/jobs/manage" 
              element={user ? <JobManagement /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/post-job" 
              element={user?.userType === 'customer' ? <JobPosting /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/terms-and-conditions" 
              element={<TermsAndConditions />} 
            />
            <Route 
              path="/privacy-policy" 
              element={<PrivacyPolicy />} 
            />
          </Routes>
          </Suspense>
          <Toaster />
          <PWAInstallPrompt />
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;